import React, { useState } from 'react';
import {
    Paper,
    Box,
    Typography,
    Button,
    Grid,
    TextField,
    Divider,
    InputLabel,
    Select,
    MenuItem,
    Link,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { getAuth } from '../../contexts/authStore';
import {
    Edit as EditIcon,
    Cancel as CancelIcon,
    Save as SaveIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    CheckCircle,
} from '@mui/icons-material';
import moment from 'moment';
import { getAttributeVerificationCode } from '../../lib/authenticate';

interface ProfileTabProps {
    editing: boolean;
    profileData: Record<string, any>;
    oldProfileData: Record<string, any>;
    schedules: Record<string, any>[] | null;
    onProfileChange: (field: string, value: any) => void;
    onEditToggle: (editing: boolean) => void;
    onSave: () => void;
    onVerifyAttribute?: (attributeName: 'email' | 'phone_number', code: string) => Promise<void>;
    onCancel: () => void;
}

export default function ProfileTab({
    editing,
    profileData,
    oldProfileData,
    schedules,
    onProfileChange,
    onEditToggle,
    onSave,
    onVerifyAttribute,
    onCancel,
}: ProfileTabProps) {
    const [verifyingAttribute, setVerifyingAttribute] = useState<'email' | 'phone_number' | null>(null);
    const [verificationCode, setVerificationCode] = useState('');
    const [isSubmittingCode, setIsSubmittingCode] = useState(false);
    const [verificationCodeSent, setVerificationCodeSent] = useState(false);

    React.useEffect(() => {
        if (!!verifyingAttribute && !verificationCodeSent) {
            getAttributeVerificationCode(verifyingAttribute);
            setVerificationCodeSent(true);
        }
    }, [verifyingAttribute])

    const handleSubmitVerificationCode = async () => {
        if (!verifyingAttribute || !verificationCode.trim()) {
            return;
        }

        try {
            setIsSubmittingCode(true);
            if (onVerifyAttribute) {
                await onVerifyAttribute(verifyingAttribute, verificationCode);
                setVerifyingAttribute(null);
                setVerificationCode('');
            }
        } catch (error) {
            console.error('Error verifying code:', error);
        } finally {
            setIsSubmittingCode(false);
        }
    };
    return (
        <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">Personal Information</Typography>
                {!editing ? (
                    <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={() => onEditToggle(true)}
                    >
                        Edit Profile
                    </Button>
                ) : (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={() => {
                                onProfileChange('_reset', oldProfileData);
                                onEditToggle(false);
                                onCancel();
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={onSave}
                        >
                            Save Changes
                        </Button>
                    </Box>
                )}
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Full Name"
                        value={profileData.name}
                        onChange={(e) => onProfileChange('name', e.target.value)}
                        disabled={!editing}
                        InputProps={{
                            startAdornment: <PersonIcon sx={{ mr: 1, color: 'action.active' }} />
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Email Address"
                        value={profileData.email}
                        onChange={(e) => onProfileChange('email', e.target.value)}
                        disabled={!editing || getAuth().email_verified?.toString() === 'true'}
                        InputProps={{
                            startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />
                        }}
                        helperText={getAuth().email_verified?.toString() === 'true' ?
                            <Typography variant="body2" color="success.main">Verified <CheckCircle sx={{ fontSize: 14, verticalAlign: 'middle' }} /></Typography> :
                            ""}
                    />
                    {profileData.email && (getAuth().email_verified?.toString() === 'false') && (
                        <Link
                            component="button"
                            variant="body2"
                            onClick={(e) => {
                                e.preventDefault();
                                setVerifyingAttribute('email');
                            }}
                            sx={{ display: 'block', mt: 1, cursor: 'pointer' }}
                        >
                            Verify Email
                        </Link>
                    )}
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Phone Number"
                        value={profileData.phone}
                        onChange={(e) => onProfileChange('phone', e.target.value)}
                        disabled={!editing || getAuth().phone_number_verified?.toString() === 'true'}
                        InputProps={{
                            startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />
                        }}
                        helperText={getAuth().phone_number_verified?.toString() === 'true' ? <Typography variant="body2" color="success.main">Verified <CheckCircle sx={{ fontSize: 14, verticalAlign: 'middle' }} /></Typography> : ""}
                    />
                    {/\+1\d{10}$/.test(profileData.phone) && (getAuth().phone_number_verified?.toString() === 'false') &&
                        oldProfileData.phone === profileData.phone && (
                            <Link
                                component="button"
                                variant="body2"
                                onClick={(e) => {
                                    e.preventDefault();
                                    setVerifyingAttribute('phone_number');
                                }}
                                sx={{ display: 'block', mt: 1, cursor: 'pointer' }}
                            >
                                Verify Phone
                            </Link>
                        )}
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Staff ID"
                        value={profileData.userId}
                        disabled
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        fullWidth
                        label="Join Date"
                        value={moment(profileData.created).format('MMMM D, YYYY')}
                        disabled
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Preferences
            </Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                    <InputLabel id="primary-schedule-label">
                        <Typography variant="caption" fontWeight="bold">
                            Primary Schedule
                        </Typography>
                    </InputLabel>
                    <InputLabel id="primary-schedule-description" sx={{ mb: 1 }}>
                        <Typography variant="caption">
                            Displays selected schedule when you log in
                        </Typography>
                    </InputLabel>
                    <Select
                        disabled={!editing}
                        fullWidth
                        value={profileData.primarySchedule || ''}
                        onChange={(e) => onProfileChange('primarySchedule', e.target.value)}
                    >
                        {schedules?.map((schedule: Record<string, any>) => (
                            <MenuItem key={schedule.scheduleId} value={schedule.scheduleId}>
                                {schedule.name}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>
            </Grid>

            {/* Verification Code Dialog */}
            <Dialog open={!!verifyingAttribute} onClose={() => !isSubmittingCode && setVerifyingAttribute(null)}>
                <DialogTitle>
                    Verify {verifyingAttribute === 'email' ? 'Email' : 'Phone Number'}
                </DialogTitle>
                <DialogContent sx={{ minWidth: 400, pt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 3 }}>
                        A 6-digit verification code has been sent to your {verifyingAttribute === 'email' ? 'email address' : 'phone number'}. Please enter it below.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Verification Code"
                        placeholder="000000"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5em' } }}
                        disabled={isSubmittingCode}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setVerifyingAttribute(null)
                    }} disabled={isSubmittingCode}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitVerificationCode}
                        variant="contained"
                        disabled={verificationCode.length !== 6 || isSubmittingCode}
                    >
                        {isSubmittingCode ? 'Verifying...' : 'Verify'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}
